import { useCallback, useEffect, useRef } from 'react';
import { useTracking } from 'react-tracking';
import type { TrackEventInput } from '@updater/ui-tracker';
import {
  useTermSelection as useCoreTermSelection,
  DEFAULT_TERM,
  type OfferCode,
  type OfferTermLengthType,
  type PlanTermLength,
  type TermSelection,
  type UseTermSelectionOptions,
} from '@updater/ui-widgets';

export {
  DEFAULT_TERM,
  getTermPartner,
  pickTermEntry,
} from '@updater/ui-widgets';
export type {
  OfferEntry,
  OfferPair,
  OfferCode,
  OfferTermLengthType,
  PlanTermLength,
  TermSelection,
} from '@updater/ui-widgets';

/**
 * The selected term per plan, keyed by the 1-year offer's code. Both the card
 * and its details modal read it, so they always agree.
 *
 * Thin wrapper: the state lives in ui-widgets; this only adds the app's
 * `compare_term_tabs` tracking for the click.
 */
export function useTermSelection(
  startingTerm: OfferTermLengthType | UseTermSelectionOptions = DEFAULT_TERM
): TermSelection {
  const options: UseTermSelectionOptions =
    typeof startingTerm === 'string' ? { startingTerm } : startingTerm ?? {};

  const { trackEvent } = useTracking<TrackEventInput<unknown>>({
    object: 'offers',
  });
  // Held in a ref so a re-render that hands back a new `trackEvent` identity
  // does not change the core hook's `setTerm`, which would re-render every
  // card reading it.
  const trackRef = useRef(trackEvent);
  useEffect(() => {
    trackRef.current = trackEvent;
  }, [trackEvent]);

  const onTermChange = useCallback(
    (_offerCode: OfferCode, term: OfferTermLengthType) => {
      trackRef.current({
        object: 'compare_term_tabs',
        verb: 'clicked',
        details: { tab: term },
      });
    },
    []
  );

  return useCoreTermSelection({ ...options, onTermChange });
}
